import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/NewButton';
import { Search, Library, FileText, Users, Briefcase, Gavel, Mail, Building, Plus, User, Globe, Pencil, Expand } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCrimsonPrompts, useCustomPrompts } from './hooks';
import { PromptFormDialog, EditPromptData } from './PromptFormDialog';
import { CategoryGroup, PromptScope } from './types';
import { Loading } from '@/components/shared';

const CATEGORY_ICONS: Record<string, any> = {
  'Pleadings': Gavel,
  'Witness Statements': Users,
  'Correspondence': Mail,
  'Contracts': FileText,
  'Court Orders': Building,
  'Expert Reports': Briefcase,
};

const SCOPE_BADGES: Record<PromptScope, { label: string; className: string; icon: any }> = {
  user: { label: 'Personal', className: 'bg-purple-100 text-purple-700 border-purple-200', icon: User },
  matter: { label: 'Matter', className: 'bg-blue-100 text-blue-700 border-blue-200', icon: Briefcase },
  tenant: { label: 'Firm', className: 'bg-green-100 text-green-700 border-green-200', icon: Globe },
};

interface PromptLibraryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPrompt: (prompt: string) => void;
  matterSlug?: string;
}

const countPrompts = (groups: CategoryGroup[]) =>
  groups.reduce((sum, g) => sum + g.prompts.length, 0);

const filterGroups = (groups: CategoryGroup[], query: string): CategoryGroup[] => {
  if (!query) return groups;
  const q = query.toLowerCase();
  return groups
    .map((group) => ({
      ...group,
      prompts: group.prompts.filter(
        (p) =>
          group.categoryName.toLowerCase().includes(q) ||
          p.title.toLowerCase().includes(q) ||
          p.content.toLowerCase().includes(q)
      ),
    }))
    .filter((group) => group.prompts.length > 0);
};

export const PromptLibrary: React.FC<PromptLibraryProps> = ({
  open,
  onOpenChange,
  onSelectPrompt,
  matterSlug,
}) => {
  const routeParams = useParams({ strict: false }) as { slug?: string };
  const slug = matterSlug ?? routeParams.slug;
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [previewPrompt, setPreviewPrompt] = useState<{ title: string; content: string } | null>(null);
  const [activeTab, setActiveTab] = useState('crimson');
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<EditPromptData | undefined>(undefined);

  const { crimsonGroups, isLoading, fetchCrimsonPrompts } = useCrimsonPrompts(slug);
  const { customGroups, isLoading: isLoadingCustom, fetchCustomPrompts } = useCustomPrompts(slug);

  useEffect(() => {
    if (open) {
      fetchCrimsonPrompts();
      fetchCustomPrompts();
    }
  }, [open, fetchCrimsonPrompts, fetchCustomPrompts]);

  const filteredCrimson = useMemo(() => filterGroups(crimsonGroups, searchQuery), [crimsonGroups, searchQuery]);
  const filteredCustom = useMemo(() => filterGroups(customGroups, searchQuery), [customGroups, searchQuery]);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleSelectPrompt = (prompt: string) => {
    onSelectPrompt(prompt);
    onOpenChange(false);
    setSearchQuery('');
  };

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setExpandedCategories(new Set());
      setPreviewPrompt(null);
    }
  }, [open]);

  const renderScopeBadge = (scope: PromptScope) => {
    const config = SCOPE_BADGES[scope];
    if (!config) return null;
    const Icon = config.icon;
    return (
      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border', config.className)}>
        <Icon className="h-2.5 w-2.5" />
        {config.label}
      </span>
    );
  };

  const renderCategoryList = (groups: CategoryGroup[]) => (
    <div className="space-y-4">
      {groups.map(({ categoryKey, categoryName, prompts }) => {
        const isExpanded = expandedCategories.has(categoryKey);
        const Icon = CATEGORY_ICONS[categoryName] || FileText;

        return (
          <div key={categoryKey} className="space-y-2">
            <button
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-lg border transition-all motion-reduce:transform-none",
                isExpanded
                  ? "bg-blue-50 border border-blue-200"
                  : "border-transparent hover:-translate-y-px hover:border-slate-200 hover:bg-slate-50 hover:shadow-sm"
              )}
              onClick={() => toggleCategory(categoryKey)}
            >
              <div className={cn(
                "p-2 rounded-lg transition-colors",
                isExpanded ? "bg-blue-100" : "bg-slate-100"
              )}>
                <Icon className={cn(
                  "h-4 w-4 transition-colors",
                  isExpanded ? "text-blue-600" : "text-slate-600"
                )} />
              </div>
              <span className="font-medium text-slate-900 flex-1 text-left">
                {categoryName}
              </span>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                {prompts.length} {prompts.length === 1 ? 'prompt' : 'prompts'}
              </span>
            </button>

            {isExpanded && (
              <div className="ml-14 space-y-2">
                {prompts.map((prompt) => (
                  <div key={prompt.key} className="group/item relative" data-testid="prompt-card">
                    <button
                      className="w-full text-left p-3 rounded-lg border border-slate-200 transition-all hover:-translate-y-px hover:border-blue-300 hover:bg-blue-50/50 hover:shadow-sm motion-reduce:transform-none"
                      onClick={() => handleSelectPrompt(prompt.content)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-slate-900">
                          {prompt.title}
                        </span>
                        {prompt.scope && renderScopeBadge(prompt.scope)}
                      </div>
                      <div className="text-sm text-slate-600 line-clamp-2">
                        {prompt.content}
                      </div>
                    </button>
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
                      <Button
                        size="xs"
                        variant="ghost"
                        icon={Expand}
                        className="text-slate-400 hover:text-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewPrompt({ title: prompt.title, content: prompt.content });
                        }}
                        title="Preview full prompt"
                      />
                      {prompt.promptGroupId && (
                        <Button
                          size="xs"
                          variant="ghost"
                          icon={Pencil}
                          className="text-slate-400 hover:text-slate-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPrompt({
                              promptGroupId: prompt.promptGroupId!,
                              title: prompt.title,
                              content: prompt.content,
                              categoryName: prompt.categoryName,
                              scope: prompt.scope!,
                              canManage: prompt.canManage,
                            });
                            setFormDialogOpen(true);
                          }}
                          title="Edit prompt"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={cn("sm:max-w-[700px] h-[78vh] flex flex-col p-0 overflow-hidden transition-opacity", formDialogOpen && "opacity-50 pointer-events-none")}>
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Library className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">Prompt Library</DialogTitle>
                <DialogDescription className="text-sm mt-1">
                  Browse and select prompts
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 pt-4 shrink-0 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search prompts..."
                className="pl-10 pr-4"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full">
                <TabsTrigger value="crimson" className="flex-1">
                  Crimson
                  <span className="ml-1.5 text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">
                    {countPrompts(searchQuery ? filteredCrimson : crimsonGroups)}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="custom" className="flex-1">
                  Custom
                  <span className="ml-1.5 text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">
                    {countPrompts(searchQuery ? filteredCustom : customGroups)}
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <ScrollArea className="flex-1 min-h-0 px-6 pb-6 pt-2">
            {activeTab === 'crimson' ? (
              isLoading ? (
                <div className="flex justify-center py-12">
                  <Loading />
                </div>
              ) : filteredCrimson.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Search className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No prompts found matching &quot;{searchQuery}&quot;</p>
                </div>
              ) : (
                renderCategoryList(filteredCrimson)
              )
            ) : (
              isLoadingCustom ? (
                <div className="flex justify-center py-12">
                  <Loading />
                </div>
              ) : filteredCustom.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  {searchQuery ? (
                    <>
                      <Search className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No custom prompts found matching &quot;{searchQuery}&quot;</p>
                    </>
                  ) : (
                    <>
                      <Library className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="mb-2">No custom prompts yet</p>
                      <p className="text-xs text-slate-400">
                        Click &quot;Add&quot; to save your first prompt, or use the save icon on any message
                      </p>
                    </>
                  )}
                </div>
              ) : (
                renderCategoryList(filteredCustom)
              )
            )}
          </ScrollArea>

          <Button
            size="sm"
            icon={Plus}
            text="Add"
            className="absolute bottom-4 right-4 shadow-md"
            onClick={() => { setEditingPrompt(undefined); setFormDialogOpen(true); }}
          />
        </DialogContent>
      </Dialog>

      <PromptFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        onPromptSaved={fetchCustomPrompts}
        editPrompt={editingPrompt}
        matterSlug={slug}
      />

      {/* Preview dialog for full prompt content */}
      <Dialog open={!!previewPrompt} onOpenChange={() => setPreviewPrompt(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[70vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle>{previewPrompt?.title}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
            <div className="text-sm text-slate-700 whitespace-pre-wrap pr-4">
              {previewPrompt?.content}
            </div>
          </ScrollArea>
          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              text="Use prompt"
              onClick={() => {
                if (previewPrompt) handleSelectPrompt(previewPrompt.content);
                setPreviewPrompt(null);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
